import * as React from 'react';
import { HiOutlineExclamationCircle } from 'react-icons/hi';
import Box from '@mui/material/Box';
import {  Typography } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Skeleton from '@mui/material/Skeleton';
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';
import { visuallyHidden } from '@mui/utils';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';
import API from '@aws-amplify/api';

import DateTimeText from '../../../../shared/DateTimeText';
import RenderIf from '../../../../renderIf/RenderIf';
import { useStyles } from './styles';
import StatusBox from '../statusBox';
import { ShipyardContext } from '../../../../Context';
import ActionDropdown from '../../../../customSelect/ActionDropDown';
import useLinkStyle from '../../../../shared/dashboardBody/components/link/styles';
import SearchField from '../../../../shared/search/components/searchField/SearchField';
import { MuiTablePagination } from '../../../../shared/muiControls';
import BatchStatusUpdate from '../../../../shared/modals/batchStatusUpdate';
import useBatchStatus from '../../../../shared/modals/batchStatusUpdate/useBatchStatus';

import ExportData from '../../../../shared/exportData';

const ApplicationsTable = (props) => {
    const {
        page,
        rowsPerPage,
        totalRecordCount,
        currentCount,
        list,
        selectedItem,
        allChecked,
        // loadMore, #TODO Bring this back if we ever need to make more than one call - SHIP-2328
        isFetching,
        isInitialized,
        // hasMore, #TODO Bring this back if we ever need to make more than one call - SHIP-2328
        sortField,
        searchTerm,
        filterString,
        preparedColumns,
        handleSearchTerm,
        statusRef,
        isScholarshipAdmin,
        sortDirection,
        isSchoolAdmin,
        handlePageChange,
        handleRowsPerPageChange,
        handleCheckbox,
        handleCheckAll,
        ctx,
    } = props;
    const linkStyle = useLinkStyle(isFetching);
    const classes = useStyles();
    const tableEl = React.useRef();
    const history = useHistory();
    const { setListSettings, userSettings } = React.useContext(ShipyardContext);
    const [batchStatusOpen, setBatchStatusOpen] = React.useState(false);
    const [exportOpen, setExportOpen] = React.useState(false);

    const { validateBatchUpdate } = useBatchStatus(ctx);

    const handleInvoiceCreation = async (listItem) => {
        const { AppInstID, AppTermSchoolPeriodID } = listItem;

        //if we have an id we dont want to start the invoice
        if (listItem.InvoiceID) {
            return history.push({
                pathname: `/finances/invoices/${AppInstID}`,
                state: { invoice: listItem, statusRef, filterString },
            });
        }

        try {
            await API.patch('ShipyardAPI', `/finances/invoices/${AppInstID}`, {
                body: {
                    pPeriodID: AppTermSchoolPeriodID,
                },
            });

            history.push({
                pathname: `/finances/invoices/${AppInstID}`,
                state: { invoice: listItem, statusRef },
            });
        } catch {
            console.log(' there was an error ');
        }
    };
    const saveListState = async () => {
        setListSettings({
            useCache: true,
            reducerState: {
                ...props,
                recOffset: 0,
            },
            target: ctx,
        });
    };

    const handleAction = async (action) => {
        switch(action) {
            case 'move':
                saveListState();
                break;
            case 'batch_update':
                let response = await validateBatchUpdate(Array.from(selectedItem));

                if (response === true) {
                    setBatchStatusOpen(true);
                }
                break;
            case 'report':
                setExportOpen(true);
                break;
            default:
                console.log('Action not yet implemented.');
        }
    };

    const handleStatusClose = async (boolUpdate) => {
        setBatchStatusOpen(false);
        if(boolUpdate) {
            props?.reload?.();
        }
    };

    const handleExportClose = () => {
        setExportOpen(false);
    };

    const getValue = (column, listItem) => {
        const getActionLabel = (status) => {
            if (status === 'Not Started' || status === 'Created') {
                if (isScholarshipAdmin) {
                    return '';
                }
                if (isSchoolAdmin) {
                    return (
                        <>
                            {` / `}
                            <span
                                onClick={() => {
                                    setListSettings({
                                        useCache: true,
                                        reducerState: {
                                            ...props,
                                            recOffset: 0,
                                        },
                                        target: ctx,
                                    });
                                    handleInvoiceCreation(listItem);
                                }}
                                className="button-link"
                            >
                                {status === 'Not Started' ? 'Start' : 'Edit'}
                            </span>
                        </>
                    );
                }
            }
            if (status === 'Returned' || status === 'Mailed' || status === 'Submitted' || status === 'Approved') {
                let actionText = '';
                if (isScholarshipAdmin) {
                    actionText = status === 'Mailed' || status === 'Submitted' ? 'View' : 'Edit';
                }

                if (isSchoolAdmin) {
                    actionText = status === 'Mailed' || status === 'Submitted' || status === 'Approved' ? 'View' : 'Edit';
                }

                return (
                    <>
                        {` / `}
                        <span
                            onClick={() => {
                                setListSettings({
                                    useCache: true,
                                    reducerState: {
                                        ...props,
                                        recOffset: 0,
                                    },
                                    target: ctx,
                                });
                                history.push({
                                    pathname: `/finances/invoices/${listItem.AppInstID}`,
                                    state: { invoice: listItem, statusRef },
                                });
                            }}
                            className="button-link"
                        >
                            {actionText}
                        </span>
                    </>
                );
            }
        };
        switch (column.id) {
            case 'DOB':
                return (
                    <Typography>
                        {!!listItem['DOB'] ? (
                            <DateTimeText
                                date={listItem['DOB']}
                                type="short"
                            />
                        ) : (
                            ''
                        )}
                    </Typography>
                );
            case 'StudentLastName':
                return (
                    <>
                        <RenderIf isTrue={listItem['Legacy']}>
                            <Typography variant="body1">{listItem.StudentLastName}</Typography>
                        </RenderIf>
                        <RenderIf isTrue={!listItem['Legacy']}>
                            <Typography
                                sx={linkStyle}
                                onClick={() => {
                                    saveListState();
                                    history.push(`/scholarship/application/${listItem.ApplicationInstanceId}?admin`);
                                }}
                            >
                                {listItem.StudentLastName}
                            </Typography>
                        </RenderIf>
                    </>
                );
            case 'StudentFirstName':
                return (
                    <>
                        <RenderIf isTrue={listItem['Legacy']}>
                            <Typography variant="body1">{listItem.StudentFirstName}</Typography>
                        </RenderIf>
                        <RenderIf isTrue={!listItem['Legacy']}>
                            <Typography
                                sx={linkStyle}
                                onClick={() => {
                                    saveListState();
                                    history.push(`/scholarship/application/${listItem.ApplicationInstanceId}?admin`);
                                }}
                            >
                                {listItem.StudentFirstName}
                            </Typography>
                        </RenderIf>
                    </>
                );
            case 'ApplicationTypeID':
                return <Typography>{listItem.ApplicationTypeId === 1 ? 'Initial' : 'Renewal'}</Typography>;
            case 'LastUpdatedUTC':
                return (
                    <Typography>
                        {!!listItem.LastUpdatedUTC ? (
                            <DateTimeText
                                date={listItem.LastUpdatedUTC}
                                type="short"
                            />
                        ) : (
                            ''
                        )}
                    </Typography>
                );
            case 'InvoiceSubmitUTC':
                return (
                    <Typography>
                        {!!listItem.InvoiceSubmitUTC ? (
                            <DateTimeText
                                date={listItem.InvoiceSubmitUTC}
                                type="short"
                            />
                        ) : (
                            ''
                        )}
                    </Typography>
                );
            case 'InvoiceAmount':
            case 'LNHAward':
                return <Typography>{`$ ${listItem[column.id] || 0}`}</Typography>;
            case 'StatusId':
                return (
                    <StatusBox
                        status={listItem.Status}
                        lastUpdated={!!listItem.LastUpdatedUTC ? format(new Date(listItem.LastUpdatedUTC || ''), 'MM/dd/yyyy') : ''}
                    />
                );
            case 'InvoiceNumber':
                return <Typography>{listItem.InvoiceNumber}</Typography>;
            case 'InvoiceStatus':
                return (
                    <StatusBox
                        status={statusRef[listItem.InvoiceStatus] || {}}
                        lastUpdated={
                            !!listItem.InvoiceLastUpdatedUTC ? format(new Date(listItem.InvoiceLastUpdatedUTC || ''), 'MM/dd/yyyy') : ''
                        }
                        labelAction={getActionLabel(listItem.InvoiceStatus)}
                    />
                );
            case 'UniqueID':
                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <RenderIf isTrue={listItem.ReviewFlag}>
                            <HiOutlineExclamationCircle
                                color={'#FA6400'}
                                style={{ fontWeight: 500, marginRight: '8px' }}
                            />
                        </RenderIf>
                        <Typography>{listItem[column.id]}</Typography>
                    </div>
                );
            default:
                return <Typography>{listItem[column.id]}</Typography>;
        }
    };

    // React.useEffect(() => {
    //   console.log("something has changed with selected Item", selectedItem)
    // }, [selectedItem])
    return (
        <Box>
            <BatchStatusUpdate
                open={batchStatusOpen}
                selected={Array.from(selectedItem)}
                closeCallback={handleStatusClose}
                ctx={ctx}
            />

            <ExportData
                open={exportOpen}
                selected={Array.from(selectedItem)}
                closeCallback={handleExportClose}
            />

            <Paper>
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', m: '16px' }}>
                    <SearchField
                        handleSearchChange={handleSearchTerm}
                        savedSearch={searchTerm}
                        sx={{ margin: '24px 0px' }}
                    />

                    <RenderIf isTrue={userSettings.UserType === 3}>
                        <Box sx={{ margin: '24px 48px' }}>
                            <ActionDropdown
                                selectedItem={selectedItem}
                                listExitCallback={handleAction}
                                ctx={ctx}
                            />
                        </Box>
                    </RenderIf>
                </Box>
                <TableContainer
                    sx={{ margin: '16px', maxHeight: '55vh' }}
                    ref={tableEl}
                >
                    <Table
                        aria-labelledby="tableTitle"
                        size={'small'}
                        sx={{ width: '98%' }}
                        stickyHeader
                    >
                        <TableHead>
                            <TableRow>
                                <RenderIf isTrue={userSettings.UserType === 3}>
                                    <TableCell
                                        sx={{padding: '8px 16px'}}
                                        key={'checkbox-row'}
                                    >
                                        <RenderIf isTrue={isFetching}>
                                            <Box />
                                        </RenderIf>
                                        <RenderIf isTrue={!isFetching}>
                                            <Checkbox
                                                checked={allChecked}
                                                onChange={handleCheckAll}
                                            />
                                        </RenderIf>
                                    </TableCell>
                                </RenderIf>
                                {preparedColumns.map((headCell, index) => {
                                    return (
                                        <TableCell
                                            sx={{ padding: '8px 16px', fontWeight: 700 }}
                                            key={headCell.id}
                                            align={headCell.numeric ? 'right' : 'left'}
                                        >
                                            <TableSortLabel
                                                active={sortField === headCell.id}
                                                direction={sortDirection?.toLowerCase()}
                                                onClick={() => headCell.handleSort(headCell.id)}
                                                className={classes.headerLabels}
                                            >
                                                {headCell.label}
                                                {sortField === headCell.id ? (
                                                    <Box
                                                        component="span"
                                                        sx={visuallyHidden}
                                                    >
                                                        {sortDirection === 'Desc' ? 'sorted descending' : 'sorted ascending'}
                                                    </Box>
                                                ) : null}
                                            </TableSortLabel>
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <RenderIf isTrue={list?.length > 0}>
                                {list
                                ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                ?.map((listItem, index) => {
                                    const labelId = `enhanced-table-checkbox-${index}`;
                                    return (
                                        <TableRow
                                            hover
                                            sx={{ height: '45px', width: '100%' }}
                                            tabIndex={-1}
                                            key={'tr' + index}
                                        >
                                            <RenderIf isTrue={userSettings.UserType === 3}>
                                                <TableCell
                                                    component="th"
                                                    id={`checkbox-${index}`}
                                                    scope="row"
                                                    sx={{ width: '20px' }}
                                                >
                                                    <Checkbox
                                                        onChange={(e) => handleCheckbox?.(e, listItem)}
                                                        checked={selectedItem?.has(listItem)}
                                                    />
                                                </TableCell>
                                            </RenderIf>
                                            {preparedColumns.map((column, i) => (
                                                <TableCell
                                                    component="th"
                                                    key={`${labelId}-${i}`}
                                                    id={labelId}
                                                    scope="row"
                                                >
                                                    {getValue(column, listItem)}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    );
                                })}
                            </RenderIf>

                            <RenderIf isTrue={list?.length === 0 && isInitialized && !isFetching}>
                                <TableRow>
                                    <TableCell
                                        align="center"
                                        colSpan={preparedColumns.length}
                                    >
                                        No records to show.
                                    </TableCell>
                                </TableRow>
                            </RenderIf>
                            
                            <RenderIf isTrue={isFetching}>
                                {[...Array(10).fill('')].map((_c, ri) => (
                                    <TableRow
                                        hover
                                        sx={{ height: '45px', width: '100%' }}
                                        tabIndex={-1}
                                        key={'skele-row-' + ri}
                                    >
                                        {[...Array(10).fill('')].map((column, i) => (
                                            <TableCell
                                                component="th"
                                                id={'skeleton' + i}
                                                key={'skeleton' + i}
                                                scope="row"
                                            >
                                                <Skeleton />
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </RenderIf>
                        </TableBody>
                    </Table>
                </TableContainer>

                <MuiTablePagination
                    disabled={isFetching}
                    count={currentCount || 0}
                    totalCount={totalRecordCount || 0}
                    page={page}
                    onPageChange={handlePageChange}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    showFirstButton={true}
                    showLastButton={true}
                />
            </Paper>
        </Box>
    );
};

export default ApplicationsTable;
